<template>
  <div>
    <table
      :class="{
        'border-thick': borderThick,
        'table-disable': disable,
      }"
    >
      <!-- Table Header Start -->
      <thead>
        <tr>
          <th
            v-for="(h, i) in headers"
            :key="i"
            :colspan="h.children.length"
            :rowspan="h.children.length ? '' : '2'"
            :class="{
              colspan: h.children.length,
              rowspan: h.children.length === 0,
              cursor: h.children.length === 0 && h.sortable,
              member: h.children.class,
            }"
            :style="{
              width: singleStyle ? h.sWidth : h.width ? h.width : '',
              minWidth: singleStyle ? h.sWidth : h.width ? h.width : '',
              maxWidth: singleStyle ? h.sWidth : h.width ? h.width : '',
            }"
            @click="optionChanged(h.value, h.sortable)"
          >
            <div v-if="h.value === 'checkbox'">
              <c-checkbox
                :value="isCheckedAll"
                :disabled="checkboxDisabled"
                width="20px"
                height="20px"
                @click="checkAll"
              />
            </div>
            <div
              v-else
              :class="{
                'd-flex justify-space-between align-center':
                  h.children.length === 0,
              }"
            >
              <span>{{ h.text }}</span>
              <div
                v-if="h.children.length === 0"
                class="sort"
                :class="{
                  active: sort === h.value,
                  desc: sort === h.value && order === 'desc',
                }"
              >
                <img
                  v-if="disable"
                  src="/img/icon_arrow_down_disable.svg"
                  alt="icon-disable"
                />
                <img
                  v-else-if="h.sortable"
                  src="/img/icon_arrow_up_blue.svg"
                  alt="icon"
                />
              </div>
            </div>
          </th>
        </tr>
        <tr>
          <th
            v-for="(c, i) in getChildrenList()"
            :key="i"
            :class="{ cursor: c.sortable, member: c.class }"
            style="height: 37px"
            :style="{
              width: singleStyle ? c.sWidth : c.width ? c.width : '',
              minWidth: singleStyle ? c.sWidth : c.width ? c.width : '',
              maxWidth: singleStyle ? c.sWidth : c.width ? c.width : '',
            }"
            @click="optionChanged(c.value, c.sortable)"
          >
            <div class="d-flex justify-space-between align-center">
              <span>{{ c.text }}</span>
              <div
                class="sort"
                :class="{
                  active: sort === c.value,
                  desc: sort === c.value && order === 'desc',
                }"
              >
                <img
                  v-if="c.sortable"
                  src="/img/icon_arrow_up_blue.svg"
                  alt="icon"
                />
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <!-- Table Header End -->

      <!-- Table Body Start -->
      <tbody>
        <tr v-if="datas.length === 0" class="no-data">
          <td :colspan="getOriginalHeaderList().length">
            <div
              v-if="disable"
              class="d-flex justify-center align-center"
              style="width: 100%; pointer-events: all !important"
            >
              <slot name="disable"></slot>
            </div>
            <div v-else-if="loading">
              <c-lottie :options="defaultOptions" :width="20" :height="20" />
            </div>
            <div
              v-else-if="dropzone"
              class="no-data-dropzone d-flex justify-center align-center flex-column"
            >
              <img
                src="/img/dataset/icon_drop_gray.svg"
                alt="icon"
                draggable="false"
                style="margin-bottom: 10px"
              />
              파일 끌어다 놓기
            </div>
            <div v-else>{{ noDataText }}</div>
          </td>
        </tr>
        <tr
          v-else
          v-for="(data, i) in datas"
          :key="i"
          :class="{ disable: data.state === '비활성', 'cursor-auto': cursor }"
          @click.stop.prevent="clickTableRow($event, data)"
          @dblclick.stop.prevent="dblClickTableRow($event, data)"
        >
          <td
            v-for="(h, j) in getOriginalHeaderList()"
            :key="j"
            :align="h.align"
            :style="{
              cursor: h.cursor,
              width: singleStyle ? h.sWidth : h.width ? h.width : '',
              minWidth: singleStyle ? h.sWidth : h.width ? h.width : '',
              maxWidth: singleStyle ? h.sWidth : h.width ? h.width : '',
            }"
            :class="h.class ? h.class : ''"
          >
            <!-- Custom Slot Start -->
            <div v-if="isCustomSlot(h.value)">
              <slot :name="h.value" :item="data[h.value]" :data="data"></slot>
            </div>
            <!-- Custom Slot End -->
            <div v-else>
              <div :class="h.class ? h.class : ''">
                <span
                  :title="h.title ? data[h.value] : ''"
                  v-text="data[h.value]"
                ></span>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
      <!-- Table Body End -->
    </table>
  </div>
</template>

<script>
import CheckBox from "@/components/CheckBox.vue";
import CLottie from "@/components/Lotties/CLottie.vue";
import * as LottieData from "@/assets/lotties/loading-spinner-blue.json";

export default {
  components: {
    "c-checkbox": CheckBox,
    "c-lottie": CLottie,
  },
  props: {
    singleStyle: {
      type: Boolean,
    },
    /** 테이블 헤더 (required) */
    headers: {
      type: Array,
      required: true,
    },
    /** 테이블 데이터
     * @property {String} text      (required)
     * @property {String} value     (required)
     * @property {Boolean} sortable (required)
     * @property {Array} children   (required)
     * @property {Boolean} title
     * @property {String} cursor
     */
    datas: {
      type: Array,
      default: () => [],
    },
    /** 링크, 콜백 등 기능이 필요한 slot (name, slotName) */
    customSlot: {
      type: Array,
      default: () => [],
    },
    /** 초기 정렬 */
    defaultSort: {
      type: String,
      default: () => "",
    },
    sortOrder: {
      type: String,
      default: () => "asc",
    },
    /** 데이터가 없는 경우 대체 텍스트 */
    noDataText: {
      type: String,
      default: () => "",
    },
    /** 작업현황, 담당자인 경우 두번째 열 border style 적용 */
    borderThick: {
      type: Boolean,
      default: () => false,
    },
    /** 테이블 disable */
    disable: {
      type: Boolean,
      default: () => false,
    },
    /** 드래그앤드롭 */
    dropzone: {
      type: Boolean,
      default: () => false,
    },
    /** 테이블 데이터 없을 경우 로딩 */
    loading: {
      type: Boolean,
      default: () => false,
    },
    /** 전체 커서 */
    cursor: {
      type: Boolean,
      default: () => false,
    },

    /** 체크박스 */
    isCheckedAll: {
      type: Boolean,
      default: () => false,
    },
    /** 체크박스 비활성화 */
    checkboxDisabled: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => {
    return {
      sort: "",
      order: "asc",

      defaultOptions: {
        animationData: LottieData,
      },
    };
  },

  mounted() {
    this.sort = this.defaultSort;
    this.order = this.sortOrder;
  },
  watch: {
    disable() {
      this.sort = this.defaultSort;
      this.order = "asc";
    },
    sortOrder(newOrder) {
      this.order = newOrder;
    },
  },
  methods: {
    getChildrenList() {
      let childList = [];
      for (let h of this.headers) {
        childList.push.apply(childList, h.children);
      }
      return childList;
    },
    getOriginalHeaderList() {
      let headerList = [];
      for (let h of this.headers) {
        if (h.value) {
          headerList.push({ ...h });
        } else {
          for (let c of h.children) {
            headerList.push({ ...c });
          }
        }
      }
      return headerList;
    },
    isCustomSlot(value) {
      return this.customSlot.indexOf(value) > -1;
    },

    /** 정렬 옵션 변경 */
    optionChanged(sort, sortable) {
      if (!sortable) return;

      if (this.sort === sort) {
        this.order = this.order === "asc" ? "desc" : "asc";
      } else {
        this.order = "desc";
      }
      this.sort = sort;

      this.$emit("optionChanged", {
        sort: this.sort,
        order: this.order,
      });
    },

    /** 테이블 행 클릭 이벤트 */
    clickTableRow(e, data) {
      this.$emit("clickTableRow", e, data);
    },

    /** 테이블 행 더블클릭 이벤트 */
    dblClickTableRow(e, data) {
      this.$emit("dblClickTableRow", e, data);
    },

    /** 체크박스 전체선택 */
    checkAll() {
      this.$emit("checkAll");
    },
  },
};
</script>

<style scoped>
table {
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
}

/* disable */
table.table-disable * {
  color: #d6d6d6 !important;
}

table.table-disable tr th {
  background-color: #fcfcfc !important;
}
/* disable */

table .cursor {
  cursor: pointer;
}

table .cursor:hover .sort {
  opacity: 1;
}

table thead tr th {
  padding: 0 12px;
  height: 45px;
  background-color: #ebfafd;
  border-right: 1px solid #eee;
  user-select: none;
}
table thead tr th:first-child,
table tbody tr td:first-child {
  border-left: 0;
}
table thead tr th:last-child,
table tbody tr td:last-child {
  border-right: 0;
}
table thead tr th.colspan {
  text-align: center;
  height: 20px;
  font-size: 12px;
  background-color: #ceefeb;
  border-right: 1px solid #d6d6d6;
}
table thead tr th.colspan:last-child {
  border-right: 0;
}
table thead tr th span {
  font-weight: 600;
  color: #233ca0;
}

table thead tr th .sort {
  width: 12px;
  height: 12px;
  transition: opacity 0.1s, transform 0.3s;
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
/** sort icon size */
table thead tr th .sort img {
  width: 12px;
}

table thead tr th .sort.active {
  opacity: 1;
}

table thead tr th .sort.desc {
  transform: rotateX(180deg);
}

table.border-thick thead tr th.rowspan:nth-child(2),
table.border-thick tbody tr td:nth-child(2) {
  border-right: 1px solid #d6d6d6;
}

table tbody tr {
  height: 60px !important;
  transition: 0.1s;
}

table:not(.table-disable) tbody tr:not(.no-data):hover {
  background-color: #f5f5f5;
}
table tbody tr:last-child td {
  border-bottom: 1px solid #eee;
}
table tbody tr.disable td {
  color: #d6d6d6 !important;
}

table tbody tr.cursor-auto,
table tbody tr.cursor-auto > * {
  cursor: auto !important;
}

table tbody tr.no-data {
  height: 600px !important;
  text-align: center;
}

table tbody tr.dropzone-active {
  height: 600px;
}

table tbody tr .no-data-dropzone {
  font-weight: 500;
  color: #999;
}

table tbody tr td {
  padding: 0px 12px;
  height: 60px;
  color: #666;
  border: 1px solid #eee;
}

table tbody tr:first-child td {
  border-top: none;
}

table tbody tr td div.date {
  width: 86px;
  line-height: 1rem;
}

table tbody tr td div.date span {
  display: flex;
  font-size: 12px;
  height: 27px;
  word-break: keep-all;
  align-items: center;
  white-space: break-spaces;
}

table thead tr th.member,
table tbody tr td.member {
  border-right: 1px solid #d6d6d6;
}
table thead tr th.member:last-child,
table tbody tr td.member:last-child {
  border-right: 0;
}
</style>
